/**
 * The Reconciliations Agent panel. SPEC-0004 §1.
 *
 * Rendered as the `drawer-side` of the shell's daisyUI drawer, so it opens
 * *over* the current page without unmounting it — the agent is consulted about
 * what is on screen, so navigating away to ask would defeat the point.
 *
 * The conversation lives here, in component state, for the life of the tab.
 * Nothing is persisted: putting customer financial discussion into Firestore is
 * a data-retention decision nobody has made, and SPEC-0004 leaves it open
 * rather than answering it by accident.
 */
import { useEffect, useRef, useState } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AGENT_SUGGESTED_PROMPTS, type AgentMessage, type AgentToolCall } from '@picklii/shared';
import { ApiError, api } from '../lib/api';
import { Icon } from './Icon';

/** A turn as the panel shows it: the message plus what produced it. */
interface Turn extends AgentMessage {
	toolCalls?: AgentToolCall[];
	truncated?: boolean;
}

/**
 * How the agent's markdown renders inside a chat bubble.
 *
 * The server's system instruction asks the model for short markdown lists
 * (`apps/api/src/agent/gemini.ts`), so the panel has to render markdown or the
 * asterisks and dashes arrive literal — the two halves of the feature have to
 * agree on the format.
 *
 * Every element is styled explicitly rather than by a `prose` class: Tailwind's
 * preflight strips list markers, heading sizes and margins, and the typography
 * plugin that would put them back is not installed. Left unstyled, a bulleted
 * list renders as unmarked, unindented lines — markdown parsed but not visibly
 * formatted, which is barely better than not parsing it.
 *
 * `react-markdown` does not render raw HTML unless `rehype-raw` is added, and it
 * deliberately is not: model output is untrusted input like any other, and the
 * agent has no reason to emit HTML. `remark-gfm` is what makes tables, strikethrough
 * and autolinks work — the model reaches for a table when comparing runs.
 *
 * Code, quote and rule surfaces are tints of `base-content` rather than a named
 * surface token. daisyUI's `chat-bubble` is itself `base-300`, so `bg-base-300`
 * on a code span painted it exactly the colour it sits on and the chip vanished
 * — in both themes. A tint of the *foreground* is legible against whatever
 * surface it lands on, which is what a bubble whose background may change needs.
 *
 * `node` is destructured away from every override because react-markdown passes
 * the AST node alongside the DOM props, and spreading it onto a real element
 * makes React warn about an unknown attribute.
 */
const MARKDOWN_COMPONENTS: Components = {
	p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
	ul: ({ node, ...props }) => <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0" {...props} />,
	ol: ({ node, ...props }) => (
		<ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0" {...props} />
	),
	li: ({ node, ...props }) => <li className="marker:opacity-60" {...props} />,
	strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
	// Headings are discouraged by the instruction but not impossible. Kept close
	// to body size: a bubble is too small for a real heading ramp.
	h1: ({ node, ...props }) => <h1 className="mt-3 mb-1 font-semibold first:mt-0" {...props} />,
	h2: ({ node, ...props }) => <h2 className="mt-3 mb-1 font-semibold first:mt-0" {...props} />,
	h3: ({ node, ...props }) => <h3 className="mt-3 mb-1 font-semibold first:mt-0" {...props} />,
	code: ({ node, ...props }) => (
		<code className="bg-base-content/10 rounded px-1 py-0.5 text-[0.85em]" {...props} />
	),
	// The `code` override above also fires inside a fenced block, so the block's
	// own background comes from `pre` and the two nest without doubling up.
	pre: ({ node, ...props }) => (
		<pre
			className="bg-base-content/10 rounded-box mb-2 overflow-x-auto p-2 text-xs last:mb-0"
			{...props}
		/>
	),
	a: ({ node, ...props }) => (
		<a className="link" target="_blank" rel="noreferrer noopener" {...props} />
	),
	blockquote: ({ node, ...props }) => (
		<blockquote
			className="border-base-content/20 mb-2 border-l-2 pl-3 opacity-80 last:mb-0"
			{...props}
		/>
	),
	// A 26rem panel cannot widen for a table, so it scrolls inside the bubble
	// rather than forcing the bubble past the panel edge.
	table: ({ node, ...props }) => (
		<div className="mb-2 overflow-x-auto last:mb-0">
			<table className="table table-xs" {...props} />
		</div>
	),
	hr: ({ node, ...props }) => <hr className="border-base-content/20 my-2" {...props} />,
};

/**
 * What the agent looked at, collapsed. SPEC-0004 §2.4.
 *
 * Not diagnostics — provenance. "Trust the number" is this product's whole
 * proposition, so a grounded claim has to be distinguishable from an ungrounded
 * one, which means the user can always see which tools actually ran.
 */
function ToolTrail({ calls }: { calls: AgentToolCall[] }) {
	return (
		<div className="collapse-arrow bg-base-200 collapse mt-2 text-xs">
			<input type="checkbox" />
			<div className="collapse-title min-h-0 py-2 text-xs font-medium">
				Looked at {calls.length} {calls.length === 1 ? 'source' : 'sources'}
			</div>
			<div className="collapse-content">
				<ul className="flex flex-col gap-1">
					{calls.map((call, i) => (
						<li key={`${call.name}-${i}`} className="flex flex-wrap items-center gap-1">
							<code className="text-[0.7rem]">{call.name}</code>
							{Object.keys(call.args).length > 0 && (
								<span className="opacity-60">{JSON.stringify(call.args)}</span>
							)}
							{call.failed === true && <span className="badge badge-warning badge-xs">failed</span>}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}

export function AgentPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
	const [turns, setTurns] = useState<Turn[]>([]);
	const [draft, setDraft] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const endRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	// Escape closes, matching the overlay and the close control. Bound only while
	// open, so it cannot swallow Escape from anything else on the page.
	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open, onClose]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		endRef.current?.scrollIntoView({ block: 'end' });
	}, [turns, busy]);

	async function send(text: string) {
		const content = text.trim();
		if (content === '' || busy) return;

		// What goes up is the plain messages. Tool calls are display metadata —
		// the server rebuilds its own tool context and must not be handed a
		// client-supplied account of what the tools returned.
		const history: AgentMessage[] = [
			...turns.map(({ role, content: previous }) => ({ role, content: previous })),
			{ role: 'user' as const, content },
		];

		setTurns((prev) => [...prev, { role: 'user', content }]);
		setDraft('');
		setError(null);
		setBusy(true);

		try {
			const reply = await api.agentChat({ messages: history });
			setTurns((prev) => [
				...prev,
				{
					role: 'assistant',
					content: reply.message.content,
					toolCalls: reply.toolCalls,
					truncated: reply.truncated,
				},
			]);
		} catch (cause) {
			// Surfaced as an error, never as an assistant message. A failure
			// rendered as prose is indistinguishable from an answer — precisely the
			// silent failure this product exists to oppose. SPEC-0004 §2.5.
			setError(
				cause instanceof ApiError
					? cause.message
					: 'Could not reach the agent. Your reconciliation data is unaffected.',
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		/*
		 * An explicit width, not `w-full`: the drawer gives its side a
		 * `max-content` grid column, so a percentage width would resolve against a
		 * track that is itself sized from this element. `max-w-[85vw]` keeps it
		 * usable on a narrow screen, where pushing the page aside leaves little
		 * room. The left border is the only thing separating the panel from the
		 * page now that there is no dimmed overlay behind it.
		 */
		<aside className="bg-base-100 border-base-300 flex h-full w-[26rem] max-w-[85vw] flex-col border-l">
			{/*
			 * Same `navbar` sizing as the app's own header, with no `min-h-0` or
			 * vertical-padding override: daisyUI gives `.navbar` a `min-height` of
			 * 4rem, and shortening this one made its bottom border sit above the
			 * shell's, so the two met at the panel edge in a visible step.
			 *
			 * Deliberately NOT `navbar-start` / `navbar-end`, which is the other way
			 * that step comes back. Those are `width: 50%` each — 13rem inside a 26rem
			 * panel — which the icon, gap and title together exceed, so the title
			 * wrapped to two lines. Two lines plus the navbar's own 1rem of padding
			 * clears the 4rem floor, while the shell's tallest child stays under it,
			 * and the two borders end up a pixel apart. A plain flex row with
			 * `justify-between` sizes to content instead, so the title stays on one
			 * line and the floor governs the height on both sides.
			 */}
			<div className="navbar bg-base-200 border-base-300 shrink-0 justify-between border-b px-4">
				<div className="flex min-w-0 items-center gap-2">
					<Icon name="sparkles" className="text-primary size-5 shrink-0" />
					<span className="truncate font-semibold whitespace-nowrap">Reconciliations Agent</span>
				</div>
				<button
					className="btn btn-square btn-ghost btn-sm tooltip tooltip-left shrink-0"
					data-tip="Close"
					onClick={onClose}
					aria-label="Close the agent">
					<Icon name="close" className="size-5" />
				</button>
			</div>

			<div className="flex grow flex-col gap-3 overflow-y-auto p-4">
				{turns.length === 0 && (
					<div className="flex flex-col gap-3">
						<p className="text-sm opacity-70">
							Ask about your reconciliations. Every figure comes from your runs and findings, read
							at the time you ask — never from memory.
						</p>
						<ul className="menu bg-base-200 rounded-box w-full gap-1 p-2">
							{AGENT_SUGGESTED_PROMPTS.map((prompt) => (
								<li key={prompt}>
									{/* A suggestion is a shortcut, not a draft: it submits. §1.4 */}
									<button className="text-left text-sm" onClick={() => void send(prompt)}>
										{prompt}
									</button>
								</li>
							))}
						</ul>
					</div>
				)}

				{turns.map((turn, i) => (
					<div key={i} className={turn.role === 'user' ? 'chat chat-end' : 'chat chat-start'}>
						{/*
						 * Only the agent's side is parsed as markdown. What the user typed
						 * is shown exactly as they typed it — silently reinterpreting their
						 * asterisks as emphasis would misquote them back to themselves, and
						 * `whitespace-pre-wrap` is what preserves their line breaks.
						 */}
						{turn.role === 'user' ? (
							<div className="chat-bubble chat-bubble-primary whitespace-pre-wrap">
								{turn.content}
							</div>
						) : (
							<div className="chat-bubble">
								<Markdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
									{turn.content}
								</Markdown>
							</div>
						)}
						{turn.role === 'assistant' && (
							<div className="chat-footer w-full">
								{turn.truncated === true && (
									<span className="badge badge-warning badge-sm mt-1">Incomplete answer</span>
								)}
								{turn.toolCalls !== undefined && turn.toolCalls.length > 0 && (
									<ToolTrail calls={turn.toolCalls} />
								)}
							</div>
						)}
					</div>
				))}

				{busy && (
					<div className="chat chat-start">
						<div className="chat-bubble">
							<span className="loading loading-dots loading-sm" />
						</div>
					</div>
				)}

				{error !== null && (
					<div role="alert" className="alert alert-error alert-soft">
						<Icon name="warning" className="size-5" />
						<span className="text-sm">{error}</span>
					</div>
				)}

				<div ref={endRef} />
			</div>

			<form
				className="border-base-300 flex items-end gap-2 border-t p-4"
				onSubmit={(event) => {
					event.preventDefault();
					void send(draft);
				}}>
				<textarea
					ref={inputRef}
					className="textarea textarea-bordered max-h-40 min-h-0 w-full grow resize-none"
					rows={2}
					placeholder="Ask about your reconciliations…"
					value={draft}
					// Disabled while a turn is in flight: an input that looks live but
					// silently discards keystrokes is worse than one visibly waiting.
					disabled={busy}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={(event) => {
						// Enter sends, Shift+Enter breaks the line — the convention
						// everywhere else people type at an assistant.
						if (event.key === 'Enter' && !event.shiftKey) {
							event.preventDefault();
							void send(draft);
						}
					}}
				/>
				{/* `tooltip-left`, not the default top: this button sits against the
				    panel's right edge, where a centred tip would run off it. */}
				<button
					type="submit"
					className="btn btn-primary btn-square tooltip tooltip-left"
					data-tip="Send"
					disabled={busy || draft.trim() === ''}
					aria-label="Send">
					{busy ? <span className="loading loading-spinner loading-sm" /> : <Icon name="send" />}
				</button>
			</form>
		</aside>
	);
}
